import { useEffect } from 'react';
import { SITE_URL } from '../../config/site';

/**
 * Per-page document head: title, description, robots, Open Graph, Twitter,
 * canonical and JSON-LD.
 *
 * This used to render react-helmet-async's <Helmet>. Once the route tree was
 * code-split, that stopped emitting anything in production: when a lazy route
 * suspends, React throws away the render, and Helmet's render-phase
 * registration goes with it. The retry render never re-collected, so pages
 * shipped with only the tags baked into index.html — no per-page title, and
 * no canonical at all.
 *
 * Managing the head from an effect sidesteps that entirely: effects only run
 * on committed renders, so suspension can't lose the update. Every tag this
 * writes is marked data-ph so it can be cleaned up on unmount without
 * touching the static tags in index.html.
 *
 * The props are unchanged, so existing call sites keep working.
 */

const MARK = 'data-ph';

/** Create-or-update a tag matching `selector`, marking it as ours. */
function upsert(selector, tagName, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute(MARK, '');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === undefined || v === null || v === false) el.removeAttribute(k);
    else el.setAttribute(k, String(v));
  });
  return el;
}

const meta = (name, content) =>
  content ? upsert(`meta[name="${name}"]`, 'meta', { name, content }) : null;

const prop = (property, content) =>
  content ? upsert(`meta[property="${property}"]`, 'meta', { property, content }) : null;

function PageHelmet({
  title,
  description,
  canonical,
  image,
  robots = 'index,follow',
  structuredData,
}) {
  const fullTitle = title ? `${title} | ByteGurukul` : 'ByteGurukul';

  // Canonical must not carry query strings or hashes: /courses?search=react
  // and /courses are the same document, and indexing both splits ranking.
  const currentUrl =
    typeof window !== 'undefined' && window.location
      ? `${window.location.origin}${window.location.pathname}`
      : SITE_URL;

  const canonicalUrl = canonical || currentUrl;
  const imageUrl = image || `${SITE_URL}/social-share.png`;

  const jsonLd = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  // Serialising here keeps the dependency a primitive, so the effect only
  // re-runs when the schema actually changes.
  const jsonLdKey = jsonLd.length ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    document.title = fullTitle;

    meta('description', description);
    meta('robots', robots);

    prop('og:type', 'website');
    prop('og:title', fullTitle);
    prop('og:description', description);
    prop('og:url', canonicalUrl);
    prop('og:image', imageUrl);

    meta('twitter:card', 'summary_large_image');
    meta('twitter:title', fullTitle);
    meta('twitter:description', description);
    meta('twitter:image', imageUrl);

    upsert('link[rel="canonical"]', 'link', { rel: 'canonical', href: canonicalUrl });

    // Replace any JSON-LD we previously wrote, then add this page's.
    document.head
      .querySelectorAll(`script[type="application/ld+json"][${MARK}]`)
      .forEach((el) => el.remove());

    JSON.parse(jsonLdKey || '[]').forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute(MARK, '');
      // Escaping the angle brackets stops a "</script>" inside any string
      // value from closing the tag early.
      script.textContent = JSON.stringify(schema)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e');
      document.head.appendChild(script);
    });

    return () => {
      // Only remove tags this component created. The defaults baked into
      // index.html carry no marker and are left alone, so a crawler that
      // arrives mid-transition still sees sensible values.
      document.head
        .querySelectorAll(`script[type="application/ld+json"][${MARK}]`)
        .forEach((el) => el.remove());
    };
  }, [fullTitle, description, robots, canonicalUrl, imageUrl, jsonLdKey]);

  return null;
}

export default PageHelmet;
